import { makeAxiosClient } from 'lib/api/makeAxiosClient'
import { domains } from 'config/env'
import { formatError } from 'utils/formatError'
import { isFlagOn, PUBLISHED_CONTENT_FLAG } from 'utils/featureFlags'
import { DWB_PROGRAM_TYPE, K5_PROGRAM_TYPE } from 'utils/program/types'
import { actions } from './reducer'
import { createCurriculumContentStateKey } from './selectors'

const getClient = () => makeAxiosClient(domains.CURRICULUM_SERVICE)

const getAlternateElementaryDigitalWellBeingRoutePath = ({
  routePath,
  contentGroupCode,
}) => {
  if (!routePath || contentGroupCode !== K5_PROGRAM_TYPE) return null

  const digitalWellBeingSegment = '/lessons/digital-well-being/'
  if (!routePath.includes(digitalWellBeingSegment)) return null

  return routePath.replace(digitalWellBeingSegment, '/lessons/')
}

const isRetryableRouteNotFoundError = error => {
  if (error?.response?.status !== 404) return false

  const code = error?.response?.data?.code
  return (
    code === 'audienced_content_error' || code === 'route_segment_not_found'
  )
}

const getAlternateDwbProgramSupportsRoutePath = ({
  routePath,
  contentGroupCode,
}) => {
  if (!routePath || contentGroupCode !== DWB_PROGRAM_TYPE) return null

  const match = /^\/program-supports\/(.+)$/.exec(routePath)
  if (!match) return null

  return `/digital-well-being/program-supports/${match[1]}`
}

const getAlternateRoutePaths = ({ routePath, contentGroupCode }) => {
  // Known curriculum-service vs frontend path mismatches. Add static-page
  // fallbacks here (section landings, advisory activities, K5 activityList,
  // HS SEAs) when a program's authored route table disagrees with the
  // stripped path CurriculumRouteContentGetter sends.
  const alternates = [
    getAlternateElementaryDigitalWellBeingRoutePath({
      routePath,
      contentGroupCode,
    }),
    getAlternateDwbProgramSupportsRoutePath({
      routePath,
      contentGroupCode,
    }),
  ].filter(Boolean)

  return alternates.filter(
    (alternateRoutePath, index) =>
      alternateRoutePath !== routePath &&
      alternates.indexOf(alternateRoutePath) === index,
  )
}

/**
 * Fetch content from the curriculum service GET /v1/content endpoint.
 * This single call replaces both the route-service and content-service calls.
 *
 * Curriculum service expects query string params (not a JSON body) and
 * authenticates via the `Authorization: Bearer <jwt>` header attached by
 * `makeAxiosClient`.
 */
const fetchCurriculumContent = ({
  contentGroupCode,
  routePath,
  siteId,
  locale = 'en-US',
  contentGroupVersionId,
}) => {
  return async dispatch => {
    const contentStateKey = createCurriculumContentStateKey(
      contentGroupCode,
      routePath,
      siteId,
      locale,
    )
    dispatch(actions.fetchingContent({ contentStateKey }))

    try {
      const client = getClient()
      // Send exactly one of contentGroupVersionId / contentGroupCode (the
      // service rejects requests that include more than one scope field).
      const params = {
        routePath,
        locale,
        ...(siteId != null ? { siteId } : {}),
        ...(contentGroupVersionId
          ? { contentGroupVersionId }
          : { contentGroupCode }),
        ...(!isFlagOn([PUBLISHED_CONTENT_FLAG]) ? { draftMode: 'true' } : {}),
      }

      let response
      try {
        response = await client.get('/v1/content', { params })
      } catch (error) {
        if (!isRetryableRouteNotFoundError(error)) {
          throw error
        }

        const alternateRoutePaths = getAlternateRoutePaths({
          routePath,
          contentGroupCode,
        })
        if (!alternateRoutePaths.length) {
          throw error
        }

        let retryError = error
        for (const alternateRoutePath of alternateRoutePaths) {
          try {
            response = await client.get('/v1/content', {
              params: {
                ...params,
                routePath: alternateRoutePath,
              },
            })
            break
          } catch (innerError) {
            retryError = innerError
          }
        }

        if (!response) {
          throw retryError
        }
      }
      dispatch(
        actions.fetchContentSuccess({
          contentStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchContentError({
          contentStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

const clearErrors = () => {
  return async dispatch => {
    dispatch(actions.clearErrors())
  }
}

export default {
  fetchCurriculumContent,
  clearErrors,
}
